// ---------------------------------------------------------------------------
// World constants
// ---------------------------------------------------------------------------

/** Size of a single map tile in pixels. */
export const TILE_SIZE = 16;

/** Width of the game map in tiles. */
export const MAP_WIDTH = 200;

/** Height of the game map in tiles. */
export const MAP_HEIGHT = 200;

/** Size of a single farm plot area in tiles. */
export const FARM_SIZE = 16;

/** Duration of one half-cycle (day or night) in milliseconds (5 minutes). A full day/night cycle is 10 minutes. */
export const DAY_DURATION = 300_000;

/** Interval in milliseconds at which plant growth is evaluated. */
export const GROWTH_TICK = 5_000;

/** Maximum number of inventory slots a player may use. */
export const MAX_INVENTORY_SLOTS = 20;

/** Base player movement speed in pixels per second. */
export const PLAYER_SPEED = 120;

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export enum TileType {
  GRASS = 0,
  DIRT = 1,
  WATER = 2,
  SAND = 3,
  STONE_PATH = 4,
  FARM_SOIL_DRY = 5,
  FARM_SOIL_WET = 6,
  FARM_SOIL_TILLED = 7,
  FARM_SOIL_PLANTED = 8,
  FENCE_H = 9,
  FENCE_V = 10,
  FENCE_CORNER_TL = 11,
  FENCE_CORNER_TR = 12,
  FENCE_CORNER_BL = 13,
  FENCE_CORNER_BR = 14,
  GATE = 15,
}

export enum Direction {
  DOWN = 0,
  LEFT = 1,
  RIGHT = 2,
  UP = 3,
}

// ---------------------------------------------------------------------------
// State interfaces
// ---------------------------------------------------------------------------

export interface InventoryItem {
  plantId: string;
  quantity: number;
}

export interface PlayerState {
  x: number;
  y: number;
  direction: Direction;
  farmId: string;
  inventory: InventoryItem[];
  plants: string[];          // ids of plants the player has unlocked / collected
  name: string;
}

export interface FarmPlot {
  x: number;
  y: number;
  plantId?: string;          // id of the planted species (undefined if empty)
  growthStage: number;       // 0 = seed, 1 = sprout, 2 = mature, 3 = harvestable
  waterLevel: number;        // 0-5, decreases over time
  plantedAt?: number;        // timestamp (ms) when the seed was planted
  fertilized?: boolean;      // grows 2x faster when fertilized
  quality?: number;          // 0-100, affects harvest yield and sentinel strength
  hasWeed?: boolean;         // must clear weed before planting
  hasSprinkler?: boolean;    // auto-waters adjacent plots
  isDefender?: boolean;      // sentinel mode: auto-attacks monsters at night
}

export interface PlacedObject {
  x: number;
  y: number;
  type: string;              // e.g. 'scarecrow', 'sprinkler', 'lamp'
}

export interface FarmState {
  id: string;
  ownerId: string;
  x: number;
  y: number;
  plots: FarmPlot[];
  objects: PlacedObject[];
}

export interface GameState {
  players: Map<string, PlayerState>;
  farms: Map<string, FarmState>;
  timeOfDay: number;         // 0-1 representing progress through the current half-cycle
  dayCount: number;          // number of full day/night cycles elapsed
}
